/**
 * GoalCard Component
 * Displays goal summary with progress indicator
 * BP-06-03: Care Team & Goals Frontend
 */

import React from 'react';
import { format, formatDistanceToNow, isPast, isToday } from 'date-fns';
import {
  Target,
  Calendar,
  User,
  MoreVertical,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import {
  Goal,
  GOAL_TYPE_LABELS,
  GOAL_STATUS_LABELS,
  PRIORITY_LABELS,
} from '@/features/goals/types/goalsTypes';

// ============================================================================
// Types
// ============================================================================

interface GoalCardProps {
  goal: Goal;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onMarkAchieved?: () => void;
  onDiscontinue?: () => void;
  showActions?: boolean;
  compact?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-sage/10 text-sage-dark',
    achieved: 'bg-green-100 text-green-700',
    discontinued: 'bg-terracotta/10 text-terracotta',
  };
  return colors[status] || colors.not_started;
}

function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    high: 'text-red-500',
    medium: 'text-yellow-500',
    low: 'text-blue-500',
  };
  return colors[priority] || 'text-gray-500';
}

function getProgressColor(percentage: number): string {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-sage';
  if (percentage >= 25) return 'bg-yellow-500';
  return 'bg-gray-300';
}

// ============================================================================
// Progress Bar Component
// ============================================================================

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${getProgressColor(percentage)}`}
        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
      />
    </div>
  );
}

// ============================================================================
// Action Menu Component
// ============================================================================

interface ActionMenuProps {
  goal: Goal;
  onEdit?: () => void;
  onDelete?: () => void;
  onMarkAchieved?: () => void;
  onDiscontinue?: () => void;
}

function ActionMenu({
  goal,
  onEdit,
  onDelete,
  onMarkAchieved,
  onDiscontinue,
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const canMarkAchieved = goal.status === 'in_progress' || goal.status === 'not_started';
  const canDiscontinue = goal.status !== 'achieved' && goal.status !== 'discontinued';

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-lg hover:bg-cream-dark transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-warm-gray" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-medium border border-warm-gray/10 py-1 min-w-[180px]">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onEdit();
                }}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit Goal
              </button>
            )}
            {canMarkAchieved && onMarkAchieved && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onMarkAchieved();
                }}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors text-green-600"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Achieved
              </button>
            )}
            {canDiscontinue && onDiscontinue && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onDiscontinue();
                }}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors text-warm-gray"
              >
                <XCircle className="w-4 h-4" />
                Discontinue
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onDelete();
                }}
                className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-cream transition-colors text-red-600"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GoalCard({
  goal,
  onClick,
  onEdit,
  onDelete,
  onMarkAchieved,
  onDiscontinue,
  showActions = true,
  compact = false,
}: GoalCardProps) {
  const targetDate = goal.targetDate ? new Date(goal.targetDate) : null;
  const isOverdue = targetDate && isPast(targetDate) && goal.status === 'in_progress';
  const isDueToday = targetDate && isToday(targetDate);

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg border border-warm-gray/10 p-5 transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:shadow-medium hover:-translate-y-1' : ''
      } ${compact ? 'p-4' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
              {GOAL_STATUS_LABELS[goal.status]}
            </span>
            {goal.isIepGoal && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-navy/10 text-navy">
                IEP
              </span>
            )}
            {goal.priority === 'high' && (
              <AlertCircle className={`w-4 h-4 ${getPriorityColor(goal.priority)}`} />
            )}
          </div>
          <h3 className="font-display font-semibold text-navy line-clamp-2">
            {goal.title}
          </h3>
        </div>

        {showActions && (onEdit || onDelete || onMarkAchieved || onDiscontinue) && (
          <ActionMenu
            goal={goal}
            onEdit={onEdit}
            onDelete={onDelete}
            onMarkAchieved={onMarkAchieved}
            onDiscontinue={onDiscontinue}
          />
        )}
      </div>

      {/* Type & Category */}
      <div className="flex items-center gap-2 text-sm text-warm-gray mb-3">
        <Target className="w-4 h-4" />
        <span>{GOAL_TYPE_LABELS[goal.goalType]}</span>
        {goal.category && (
          <>
            <span>•</span>
            <span>{goal.category}</span>
          </>
        )}
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-warm-gray">Progress</span>
          <span className="font-medium text-navy">{goal.progressPercentage}%</span>
        </div>
        <ProgressBar percentage={goal.progressPercentage} />
      </div>

      {/* Current Value */}
      {goal.currentValue && goal.targetValue && (
        <div className="flex items-center gap-2 text-sm text-navy mb-3">
          <TrendingUp className="w-4 h-4 text-sage" />
          <span>
            Current: <strong>{goal.currentValue}</strong>
          </span>
          <span className="text-warm-gray">/ Target: {goal.targetValue}</span>
        </div>
      )}

      {/* Footer */}
      {!compact && (
        <div className="flex items-center justify-between pt-3 border-t border-warm-gray/10">
          {/* Target Date */}
          {targetDate ? (
            <div
              className={`flex items-center gap-1.5 text-sm ${
                isOverdue
                  ? 'text-red-500'
                  : isDueToday
                  ? 'text-terracotta'
                  : 'text-warm-gray'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>
                {isOverdue
                  ? `Overdue by ${formatDistanceToNow(targetDate)}`
                  : isDueToday
                  ? 'Due today'
                  : `Due ${format(targetDate, 'MMM d, yyyy')}`}
              </span>
            </div>
          ) : (
            <div className="text-sm text-warm-gray">No target date</div>
          )}

          {/* Care Team Member */}
          {goal.careTeamMember && (
            <div className="flex items-center gap-1.5 text-sm text-warm-gray">
              <User className="w-4 h-4" />
              <span className="truncate max-w-[120px]">{goal.careTeamMember.name}</span>
            </div>
          )}
        </div>
      )}

      {/* IEP Area */}
      {goal.isIepGoal && goal.iepArea && !compact && (
        <div className="mt-2 text-xs text-navy-light">
          IEP Area: {goal.iepArea}
        </div>
      )}
    </div>
  );
}

export default GoalCard;

